// @noflow
import cookie from 'react-cookies';
import { isEmpty } from 'lodash';
import { dvrNotificationsEmitter } from 'Common/dvr/dvrNotifications';
import deviceType from 'Common/app/getDeviceType';
import { platform } from 'Common/player/utils';
import { removeDvrsFromCarousels } from 'Common/carousels/actions';
import {
  getJson,
  delJson,
  del,
  postJson,
  put,
  paramify,
  appendParamsToQueryString,
} from '../utilities/fetchUtils';
import { NewTimer } from '../models/NewTimer';
import type { ThunkAction } from '../types';
import { DEFAULT_RECORD_SETTINGS, type Recording, type Timer } from './types';
import {
  SKIP_OPT_VALUE_SKIPPED,
  SKIP_OPT_VALUE_SCHEDULED,
  SKIP_OPT_VALUE_CONFLICTS,
} from './TimerTypes';
import DvrEventListing from '../models/DvrEventListing';
import notify from '../utilities/reportingUtils';

export const fetchRecordings = ({
  sort = 'date',
  filter = '',
  count = '',
  sortOrder = '',
  receiverId,
}: {
  sort?: string,
  filter?: string,
  count?: number,
  sortOrder?: string,
  receiverId?: string,
} = {}): ThunkAction => ({ getState, dispatch, config }) => {
  if (deviceType === 'Tv') {
    return Promise.reject(new Error('This action is no longer used for TV'));
  }

  const filters = { sort, filter, sortOrder };
  const state = getState();
  const headers = { Accept: 'application/vnd.echostar.dvr+json;version=2' };

  if (!state.auth || !state.auth.viewer) {
    return Promise.reject(new Error('Invalid auth viewer'));
  }

  const activeReceiverId = receiverId || state.app.activeReceiver?.receiverId;
  if (!activeReceiverId) {
    return Promise.reject(new Error('Active receiver not found'));
  }

  dispatch({ type: 'REQUEST_RECORDINGS', filters });

  const url = `${config.session.radish_url}dvr`;

  const data = {};
  data.receiver_id = activeReceiverId;
  data.sort = sort;
  data.filter = filter;

  if (count > 0) {
    data.startindex = 1;
    data.offset = count;
  }

  if (sortOrder) {
    data.sortOrder = sortOrder;
  }
  return getJson({ url, headers, state, data })
    .then((payload: Array<Recording>) => {
      if (payload.error) {
        dispatch({ type: 'REQUEST_RECORDINGS_ERROR' });
        return notify({
          error: new Error(
            `fetchRecordings error: ${payload.error?.code} ${payload.error?.description}`,
          ),
        });
      }
      dispatch({ type: 'RECEIVE_RECORDINGS', payload, filters });
    })
    .catch((error: Error) => {
      notify({ error: new Error(`fetchRecordings error: ${error}`) });
      console.warn('REQUEST_RECORDINGS_ERROR', error);
      dispatch({ type: 'REQUEST_RECORDINGS_ERROR', filters });
    });
};

export const fetchRecordingsDetails = (receiverId: ?string): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();
  const activeReceiverId = receiverId || state.app.activeReceiver?.receiverId;
  if (!activeReceiverId) {
    return Promise.reject(new Error('Active receiver not found'));
  }
  const headers = { Accept: 'application/vnd.echostar.dvr+json;version=3' };

  dispatch({ type: 'REQUEST_RECORDINGS_DETAILS' });

  const url = `${config.session.radish_url}dvr?receiver_id=${activeReceiverId}`;
  return getJson({ url, headers, state })
    .then(payload => {
      dispatch({
        type: 'RECEIVE_RECORDINGS_DETAILS',
        payload,
      });
    })
    .catch(error => {
      console.warn('REQUEST_RECORDINGS_DETAILS_ERROR:', error);
      dispatch({ type: 'REQUEST_RECORDINGS_DETAILS_ERROR' });
    });
};

export const deleteRecordings = (
  id: string,
  eventListing?: DvrEventListing,
  onCompletedCallback?: boolean => void,
): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  const activeReceiverId = state.app.activeReceiver?.receiverId;
  if (!activeReceiverId) {
    return Promise.reject(new Error('Active receiver not found'));
  }
  const headers = { Accept: 'application/vnd.echostar.dvr+json;version=1' };

  dispatch({ type: 'REQUEST_DELETE_RECORDING' });

  const url = `${config.session.radish_url}dvr?receiver_id=${activeReceiverId}&id=${id}`;

  return delJson({ url, state, headers })
    .then(() => {
      dispatch({ type: 'RECEIVE_DELETE_RECORDING', id, eventListing });

      if (onCompletedCallback) {
        onCompletedCallback(true);
      }

      if (deviceType === 'Tv') {
        const { franchise, title: episodeName } = eventListing;
        dvrNotificationsEmitter.next({
          type: 'recordingDeleted',
          title:
            !isEmpty(episodeName) && !isEmpty(franchise?.name) && franchise?.name !== episodeName
              ? `${[franchise.name, episodeName].join(' • ')}`
              : franchise?.name || episodeName || '',
        });
        dispatch(removeDvrsFromCarousels(id));
      } else if (process.env.IS_BROWSER || eventListing != null) {
        dvrNotificationsEmitter.next({
          type: 'recordingDeleted',
          title: eventListing?.title,
          posterUrl: eventListing?.posterUrl,
          recordingId: id,
          seriesId: eventListing?.seriesId,
        });
      }
    })
    .catch(() => {
      if (deviceType === 'Tv') {
        const { franchise, title: episodeName } = eventListing;
        dvrNotificationsEmitter.next({
          type: 'recordingDeleteError',
          title: `${[franchise?.name, episodeName].join(' • ')}`,
        });
      }
      dispatch({ type: 'REQUEST_DELETE_RECORDING_ERROR' });
      console.warn('error', 'REQUEST_DELETE_RECORDING_ERROR');
      if (onCompletedCallback) {
        onCompletedCallback(false);
      }
    });
};
/* here we expect a string of comma separated ids of recordings to delete */
export const deleteMultipleRecordings = (
  ids: string,
  title?: string,
  onCompletedCallback?: boolean => void,
): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  const activeReceiverId = state.app.activeReceiver?.receiverId;
  if (!activeReceiverId) {
    return Promise.reject(new Error('Active receiver not found'));
  }
  const headers = { Accept: 'application/vnd.echostar.dvr+json;version=1' };

  dispatch({ type: 'REQUEST_DELETE_MULTIPLE_RECORDINGS' });

  const url = `${config.session.radish_url}dvr?receiver_id=${activeReceiverId}&ids=${ids}`;
  return del({ url, state, headers })
    .then(() => {
      dispatch({ type: 'RECEIVE_DELETE_MULTIPLE_RECORDINGS' });
      if (deviceType === 'Tv') {
        dvrNotificationsEmitter.next({ type: 'multipleRecordingDeleted', title });
        dispatch(removeDvrsFromCarousels(ids));
      }
      if (onCompletedCallback) {
        onCompletedCallback(true);
      }
    })
    .catch(() => {
      if (deviceType === 'Tv') {
        dvrNotificationsEmitter.next({ type: 'multipleRecordingDeleteError', title });
      }
      if (onCompletedCallback) {
        onCompletedCallback(false);
      }
      dispatch({ type: 'REQUEST_DELETE_MULTIPLE_RECORDINGS_ERROR' });
      console.warn('error', 'REQUEST_DELETE_MULTIPLE_RECORDINGS_ERROR');
    });
};

export const fetchTimers = (receiverId?: string): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();

  if (!state.auth || !state.auth.viewer) {
    return Promise.reject(new Error('Invalid auth viewer'));
  }

  const activeReceiverId = receiverId || state.app.activeReceiver?.receiverId;
  if (!activeReceiverId) {
    return Promise.reject(new Error('Active receiver not found'));
  }

  const apiVersion = deviceType === 'Tv' ? 2 : 1;
  const headers = { Accept: `application/vnd.echostar.timers+json;version=${apiVersion}` };

  const data = { receiver_id: activeReceiverId };

  dispatch({ type: 'REQUEST_TIMERS' });

  const url = `${config.session.radish_url}timers?sort=priority`;
  return getJson({ url, headers, state, data })
    .then((payload: Array<Timer>) => {
      if (payload.error) {
        dispatch({ type: 'REQUEST_TIMERS_ERROR' });
        return notify({
          error: new Error(
            `fetchTimers error: ${payload.error?.code} ${payload.error?.description}`,
          ),
        });
      }
      dispatch({ type: 'RECEIVE_TIMERS', payload });
    })
    .catch((error: Error) => {
      notify({ error: new Error(`fetchTimers error: ${error}`) });
      dispatch({ type: 'REQUEST_TIMERS_ERROR', error: error?.message });
    });
};

export const createTimer = (item, callback?: boolean => void): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();
  const activeReceiverId = state.app.activeReceiver?.receiverId;
  const receivers = state.auth.viewer ? state.auth.viewer.receivers : [];
  const {
    preferences: {
      timerPreferences: { preferences = {} },
    },
  } = state;
  const timer = item.modelType === 'NewTimer' ? item : new NewTimer(item);
  const { startTime = '' } = timer;
  const airingId = timer._airing?.id;
  const { endLate, maxRecordings, startEarly, priority } = preferences;
  const isActiveReceiverOnline = state.app.activeReceiver?.status?.toLowerCase() === 'online';
  const isTv = deviceType === 'Tv';
  const notifyEvent = (successful: boolean, reason?: string) =>
    (process.env.IS_BROWSER || isTv) &&
    dvrNotificationsEmitter.next({
      type: successful ? 'timerSet' : reason || 'timerSetError',
      frequency: timer.frequency,
      title: timer.programName,
      startTime,
      posterUrl: timer.posterUrl || timer?._airing?.images?.poster_url,
    });

  // TODO: Identify process on android_tv and hook up notification
  // skip notifications for mobile

  if (!activeReceiverId || isActiveReceiverOnline === false) {
    if (callback) {
      callback(false);
    }
    notifyEvent(false, 'offline');
    return Promise.reject(new Error('offline'));
  }

  dispatch({
    type: 'REQUEST_CREATE_TIMER',
    airingId,
  });

  const recordSettings = {
    priority: timer.priority || priority || DEFAULT_RECORD_SETTINGS.priority,
    minutes_early: timer.minutesEarly || startEarly || DEFAULT_RECORD_SETTINGS.startEarly,
    minutes_late: timer.minutesLate || endLate || DEFAULT_RECORD_SETTINGS.endLate,
    event_count: timer.eventCount || maxRecordings || DEFAULT_RECORD_SETTINGS.maxRecordings,
    frequency: timer.frequency || DEFAULT_RECORD_SETTINGS.frequency,
  };
  const airingData = {
    receiver_id: activeReceiverId,
    channel: timer.channel,
    start_time: timer.startTime,
    duration: timer.duration,
    upid: timer.upid,
    timer_type: timer.timerType,
    timer_attributes: timer.timerAttributes,
    creator: timer.creator,
    channel_name: timer.channelName,
    program_name: encodeURIComponent(timer.programName),
  };
  const headers = { Accept: 'application/vnd.echostar.timers+json;version=1' };
  const url = `${config.session.radish_url}timers?`;
  const data = { ...airingData, ...recordSettings };
  const id = timer._airing.franchise_id;
  const eventName = timer._airing.name;
  const identifier = {
    channel: timer.channel,
    startDateTime: timer.startTime,
  };

  return postJson({ url, state, data, headers })
    .then(res => {
      if (!isEmpty(res) && res.code.split('')[0] === '4') {
        dispatch({
          type: 'REQUEST_CREATE_TIMER_ERROR',
          airingId,
        });
        if (callback) {
          callback(false);
        }
        notifyEvent(false);
        dispatch({ type: 'SUCCESSFUL_EVENT_RESET' });
        return null;
      }
      dispatch({
        type: 'RECEIVE_CREATE_TIMER',
        airingId,
        payload: { id, eventName, identifier, frequency: recordSettings.frequency },
      });
      if (callback) {
        callback(true);
      }
      notifyEvent(true);
      dispatch({ type: 'SUCCESSFUL_EVENT_INCREMENT' });
      dispatch({ type: 'RATING_DIALOG_TRIGGER', activeReceiverId, receivers });
    })
    .catch(error => {
      dispatch({ type: 'REQUEST_CREATE_TIMER_ERROR', airingId, payload: error });
      if (callback) {
        callback(false);
      }
      notifyEvent(false);
      dispatch({ type: 'SUCCESSFUL_EVENT_RESET' });
    });
};

export const updateTimer = (item, callback?: boolean => void): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();
  const activeReceiverId = state.app.activeReceiver?.receiverId;
  if (!activeReceiverId) {
    return undefined;
  }
  const headers = { Accept: 'application/vnd.echostar.timers+json;version=1' };

  const notifyEvent = (successful: boolean, reason?: string) =>
    process.env.IS_BROWSER || deviceType === 'Tv'
      ? dvrNotificationsEmitter.next({
          type: successful ? 'timerUpdated' : reason || 'timerUpdateError',
          title: item.event_name,

          posterUrl: item.posterUrl || item?._airing?.images?.poster_url,
        })
      : () => {}; // skip notifications for mobile

  /* gTimerId is a correlation ID for handling updating on ui side */
  const gTimerId = item.gtimer_id;
  if (typeof gTimerId === 'undefined') {
    console.warn(
      `REQUEST_UPDATE_TIMER_ERROR: fail to update timer because gTimerId is not defined.`,
    );
    if (callback) callback(false);

    notifyEvent(false, 'offline');
    return Promise.reject(new Error('offline'));
  }

  dispatch({ type: 'REQUEST_UPDATE_TIMER', payload: { gTimerId, timer: item } });

  const data = {
    receiver_id: activeReceiverId,
    id: item.gtimer_id,
    channel: item.channel,
    priority: item.priority,
    frequency: item.frequency.toString() || '',
    start_time: item.start_time,
    duration: item.duration,
    minutes_early: item.minutes_early,
    minutes_late: item.minutes_late,
    upid: item.upid,
    event_count: item.event_count.toString() || '',
    timer_type: item.timer_type,
    timer_attributes: item.timer_attributes,
    creator: item.creator || 0,
    channel_name: item.channel_name,
    program_name: item.event_name,
    transcode: item.transcode || 0,
  };

  const baseUrl = `${config.session.radish_url}timers/${gTimerId}?`;
  const url = appendParamsToQueryString(baseUrl, paramify(data));
  return put({ url, state, data, headers })
    .then(response => {
      if (response.ok) {
        dispatch({ type: 'RECEIVE_UPDATE_TIMER', payload: { gTimerId, updatedTimer: item } });
        if (callback) callback(true);
        notifyEvent(true);
      } else {
        throw Error(`Error:${response.status}-${response.statusText}`);
      }
    })
    .catch(error => {
      dispatch({ type: 'REQUEST_UPDATE_TIMER_ERROR', payload: { gTimerId, timer: item } });
      if (callback) callback(false);
      notifyEvent(false);

      console.warn('error', 'REQUEST_UPDATE_TIMER_ERROR:', error.message);
    });
};

export const deleteTimer = (
  gTimerId: number,
  callback: (isSuccess: boolean) => void,
): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  const {
    auth: { viewer: { uuid } = {} },
  } = state;
  const headers = { Accept: 'application/vnd.echostar.timers+json;version=1' };
  const rs = cookie.load('rs') || uuid;

  if (!rs) {
    return Promise.reject(new Error('deleteTimer no rs'));
  }

  const timer = state.dvr.timers.content.find(timer => timer.gtimer_id === gTimerId) || {};

  dispatch({ type: 'REQUEST_DELETE_TIMER', payload: { gTimerId } });

  const url = `${config.session.radish_url}timers/${gTimerId}`;
  return delJson({ url, state, headers })
    .then(() => {
      dispatch({ type: 'RECEIVE_DELETE_TIMER', payload: { gTimerId } });
      if (callback) {
        callback(true);
      }
      dvrNotificationsEmitter.next({
        type: 'timerDeleted',
        title: timer.event_name || '',
      });
    })
    .catch(error => {
      dispatch({ type: 'REQUEST_DELETE_TIMER_ERROR', payload: { gTimerId } });
      if (callback) {
        callback(false);
      }
      dvrNotificationsEmitter.next({
        type: 'timerDeleteError',
        title: timer.event_name || '',
      });
      console.warn('error', 'REQUEST_DELETE_TIMER_ERROR:', error.message);
    });
};

export const skipRestoreTimer = (timer: Timer): ThunkAction => ({ getState, dispatch, config }) => {
  const state = getState();
  const headers = { Accept: 'application/vnd.echostar.timers+json;version=1' };
  const skipping = [SKIP_OPT_VALUE_SCHEDULED, SKIP_OPT_VALUE_CONFLICTS].includes(timer.skip_opt);
  const skipOption = skipping ? SKIP_OPT_VALUE_SKIPPED : SKIP_OPT_VALUE_SCHEDULED;

  dispatch({
    type: 'REQUEST_SKIP_RESTORE_TIMER',
  });

  const baseUrl = `${config.session.radish_url}skiptimers`;

  return put({
    url: baseUrl,
    state,
    data: { ids: timer.dtimer_id, skip_option: skipOption },
    headers,
  })
    .then(response => {
      if (response.ok) {
        dispatch({ type: 'RECEIVE_SKIP_RESTORE_TIMER_SUCCESS', timer });
        dvrNotificationsEmitter.next({
          type: skipping ? 'timerSkipped' : 'timerRestored',
          title: timer.event_name,
        });
      } else {
        throw Error(`Error:${response.status}-${response.statusText}`);
      }
    })
    .catch(error => {
      dispatch({ type: 'REQUEST_SKIP_RESTORE_TIMER_ERROR' });
      dvrNotificationsEmitter.next({ type: 'timerSkipRestoreError', title: timer.event_name });
      console.warn(`REQUEST_SKIP_RESTORE_TIMER_ERROR:${error}`);
    });
};

export const fetchDvrUsage = (receiverId: string): ThunkAction => ({
  getState,
  dispatch,
  config,
}) => {
  const state = getState();
  const headers = { Accept: 'application/vnd.echostar.receiver_info+json;version=1' };
  const data = { receiver_id: receiverId };
  const url = `${config.session.radish_url}receiver_info`;

  dispatch({ type: 'REQUEST_RECEIVER_INFO' });

  return getJson({ url, headers, state, data })
    .then((payload: ReceiverInfo) => {
      dispatch({ type: 'RECEIVE_RECEIVER_INFO', payload });
    })
    .catch((error: Error) => {
      console.warn('error', 'RECEIVE_RECEIVER_INFO_ERROR:', error.message);
      dispatch({ type: 'RECEIVE_RECEIVER_INFO_ERROR' });
    });
};

export const requestReceiversStatus = (): ThunkAction => ({ dispatch, getState, config }) => {
  const state = getState();

  const {
    auth: { viewer: { uuid } = {} },
  } = state;

  const rs = cookie.load('rs') || uuid;
  if (!rs) {
    return Promise.reject(new Error('requestReceiversStatus no rs'));
  }

  const url = `${config.session.radish_url}receiver_status?device_type=${encodeURIComponent(
    `${deviceType}-${platform}`,
  )}`;
  const headers = { Accept: 'application/vnd.echostar.receiver_status+json;version=1' };

  dispatch({ type: 'REQUEST_RECEIVERS_STATUS' });

  return getJson({ url, state, headers })
    .then((payload: Array<ReceiverStatus>) =>
      dispatch({ type: 'RECEIVE_RECEIVERS_STATUS', payload }),
    )
    .catch(err => {
      dispatch({ type: 'REQUEST_RECEIVERS_STATUS_ERROR' });
      console.warn(`REQUEST_RECEIVERS_STATUS_ERROR: ${err.message}`);
    });
};

export const changeRecordingsViewMode = (viewMode: 'grid' | 'shelf'): ThunkAction => ({
  dispatch,
}) => {
  dispatch({ type: 'CHANGE_RECORDINGS_VIEW_MODE', viewMode });
};

export const changePtatViewMode = (viewMode: 'grid' | 'shelf'): ThunkAction => ({ dispatch }) => {
  dispatch({ type: 'CHANGE_PTAT_VIEW_MODE', viewMode });
};

export const changeRecordingsSort = (sort: string): ThunkAction => ({ dispatch }) => {
  dispatch({ type: 'CHANGE_RECORDINGS_SORT', sort });
};

export const changePtatSort = (sort: string): ThunkAction => ({ dispatch }) => {
  dispatch({ type: 'CHANGE_PTAT_SORT', sort });
};
